import { useState, useMemo } from 'react';
import Select, { components } from "react-select";
import { Box, Checkbox, FormLabel, Typography } from '@mui/material';
import HelpIcon from '@mui/icons-material/Help';
import BaseColorText from '../BaseColorText';

const InputOption = ({
  getStyles,
  Icon,
  isDisabled,
  isFocused,
  isSelected,
  children,
  innerProps,
  multiple,
  ...rest
}) => {
  const [isActive, setIsActive] = useState(false);
  const onMouseDown = () => setIsActive(true);
  const onMouseUp = () => setIsActive(false);
  const onMouseLeave = () => setIsActive(false);

  // styles
  let bg = "transparent";
  if (isFocused) bg = "#C6E7FF";
  if (isActive) bg = "#C6E7FF";

  const style = {
    alignItems: "center",
    backgroundColor: bg,
    color: "inherit",
    display: "flex",
    fontFamily: "Poppins",
    fontSize: '15px',
    fontWeight: 500,
    height: '32px'
  };

  // prop assignment
  const props = {
    ...innerProps,
    onMouseDown,
    onMouseUp,
    onMouseLeave,
    style
  };

  return (
    <components.Option
      {...rest}
      isDisabled={isDisabled}
      isFocused={isFocused}
      isSelected={isSelected}
      getStyles={getStyles}
      innerProps={props}
    >
      {multiple && (
        <Checkbox checked={isSelected} />
      )}
      <Box ml="6px">
        {children}
      </Box>
    </components.Option>
  );
};

export default function BaseSelect({
  options,
  sx,
  label,
  name,
  multiple = false,
  helpIcon = false,
  inputStyles = {
    color: '#ABB7C0 !important',
    '&:placeholder': {
      color: '#ECEFF1'
    }
  },
  value,
  onChangeSelect,
  error,
  required,
  placeholder = 'Select option'
}) {
  const selectedValue = useMemo(() => {
    if (!value) return multiple ? [] : null;
    if (Array.isArray(value)) {
      return value.map(val => {
        const match = options.find(opt => opt.value === val);
        return match || { label: val, value: val };
      });
    }
    const match = options.find(opt => opt.value === value);
    return match || { label: value, value };
  }, [value, options, multiple]);

  const handleChange = (selected) => {
    if (Array.isArray(selected)) {
      onChangeSelect(selected.map((opt) => opt.value), name);
    } else {
      onChangeSelect(selected.value, name);
    }
  };

  return (
    <Box
      sx={{
        ...sx,
      }}
    >
      {label && (
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          mb: '1px',
          width: '100%',
          justifyContent: 'space-between'
        }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <FormLabel
              sx={{
                fontSize: '12px',
                fontWeight: 500,
                color: 'primary.darkGray'
              }}
              htmlFor={name}
            >
              <Typography variant='h4' color='primary.darkGray'>
                {label}
              </Typography>
              {required && <BaseColorText color='#E73E3E'>*</BaseColorText>}
            </FormLabel>
            {helpIcon && (
              <HelpIcon 
                sx={{
                  color: 'primary.mediumGray',
                  fontSize: '20px',
                  ml: '8px'
                }}
              />
            )}
            
          </Box>
          {/* <Box
            sx={{
              cursor: 'pointer',
            }}
            onClick={() => handleChange([])}
          >
            <Typography variant='body' color='primary.mainBlue'>
              Clear
            </Typography>
          </Box> */}
        </Box>
      )}
      <Select
        value={selectedValue}
        isMulti={multiple}
        placeholder={placeholder}
        closeMenuOnSelect={!multiple}
        hideSelectedOptions={false}
        onChange={handleChange}
        options={options}
        components={{
          Option: InputOption
        }}
        styles={{
          control: (baseStyles, state) => ({
            ...baseStyles,
            borderColor: state.isFocused ? '#5B87EF' : '#ABB7C0',
            borderRadius: '8px',
            fontSize: '15px',
            fontWeight: 500,
            fontFamily: "Poppins",
            minHeight: '47px',
            boxShadow: '0px 2px 10px 0px #5B87EF33',
            '&:placeholder': {
              color: '#ECEFF1 !important'
            },
            ...inputStyles
          }),
        }}
      />
      {error && (
        <Typography variant="subText" color='primary.red'>
          {error}
        </Typography>
      )}
    </Box>
  );
}